import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Phone, Mail, Globe, AtSign, Handshake, CalendarDays, Send, Loader2, Star, Quote } from "lucide-react";
import api from "@/lib/api";
import { Footer } from "@/components/Footer";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "w-full rounded-xl border border-stone_warm bg-white px-4 py-3 text-sm text-charcoal placeholder-charcoal/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function BusinessProfile() {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [status, setStatus] = useState("loading");
  const [referral, setReferral] = useState({ sender_name: "", sender_contact: "", message: "" });
  const [referralSent, setReferralSent] = useState(false);
  const [referralSending, setReferralSending] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialForm, setTestimonialForm] = useState({ author_name: "", author_business: "", text: "" });
  const [testimonialSending, setTestimonialSending] = useState(false);
  const [testimonialSent, setTestimonialSent] = useState(false);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    setStatus("loading");
    api.get(`/businesses/${id}`)
      .then((res) => { setBusiness(res.data); setStatus("ready"); })
      .catch(() => setStatus("missing"));
    api.get("/testimonials", { params: { business_id: id } })
      .then((res) => setTestimonials(res.data))
      .catch(() => {});
  }, [id]);

  const handleTestimonial = async (e) => {
    e.preventDefault();
    setTestimonialSending(true);
    try {
      const res = await api.post("/testimonials", { business_id: id, ...testimonialForm });
      setTestimonials((list) => [res.data, ...list]);
      setTestimonialForm({ author_name: "", author_business: "", text: "" });
      setTestimonialSent(true);
    } catch {
      window.alert("Could not post your endorsement. Please try again.");
    } finally {
      setTestimonialSending(false);
    }
  };

  const handleReferral = async (e) => {
    e.preventDefault();
    setReferralSending(true);
    try {
      await api.post("/referrals", { business_id: id, ...referral });
      setReferralSent(true);
    } catch {
      setReferralSent(false);
      window.alert("Could not send your request. Please try again.");
    } finally {
      setReferralSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="business-profile-page">
      <header className="fixed top-0 inset-x-0 z-50 border-b border-stone_warm bg-ivory/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" className="flex items-center gap-2.5" data-testid="profile-logo-link">
            <span className="inline-block h-2 w-2 rotate-45 bg-gold" aria-hidden="true" />
            <span className="font-serif text-xl font-semibold tracking-tight text-charcoal">Joseph Guild</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-charcoal/60 transition-colors hover:text-gold-dark"
            data-testid="profile-back-link"
          >
            <ArrowLeft size={13} /> Back to the directory
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 pb-28 pt-32 lg:px-12 lg:pt-40">
        {status === "loading" && (
          <p className="py-24 text-center font-mono text-xs uppercase tracking-[0.24em] text-charcoal/40" data-testid="profile-loading">
            Opening the listing…
          </p>
        )}

        {status === "missing" && (
          <div className="border border-dashed border-stone_warm bg-white/60 py-24 text-center" data-testid="profile-missing">
            <p className="font-serif text-3xl font-light italic text-charcoal/55">This listing is unavailable.</p>
            <Link to="/" className="mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.22em] text-gold-dark underline-offset-4 hover:underline" data-testid="profile-missing-back">
              Return to the directory
            </Link>
          </div>
        )}

        {status === "ready" && business && (
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <motion.aside
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="lg:col-span-4"
            >
              <div className="border border-stone_warm bg-white p-8 shadow-[0_8px_30px_rgba(26,26,26,0.04)] lg:sticky lg:top-28">
                {business.logo_path ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${business.logo_path}`}
                    alt={`${business.business_name} logo`}
                    className="h-24 w-24 rounded-full border border-stone_warm object-cover"
                    data-testid="profile-logo"
                  />
                ) : (
                  <span className="flex h-24 w-24 items-center justify-center rounded-full border border-stone_warm bg-[#F3F0EB]">
                    <span className="inline-block h-3 w-3 rotate-45 bg-gold" aria-hidden="true" />
                  </span>
                )}
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-gold-dark" data-testid="profile-sector">{business.sector}</p>
                {business.featured && (
                  <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-gold-dark" data-testid="profile-featured-badge">
                    <Star size={10} fill="currentColor" /> Featured member
                  </p>
                )}
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/45">by {business.owner_name}</p>

                <div className="mt-8 space-y-3 border-t border-stone_warm pt-6">
                  <a href={`tel:${business.phone}`} className="flex w-full items-center justify-center gap-2 rounded-full bg-charcoal px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-gold" data-testid="profile-call-button">
                    <Phone size={13} /> Call
                  </a>
                  <a href={`mailto:${business.email}`} className="flex w-full items-center justify-center gap-2 rounded-full border border-charcoal/25 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark" data-testid="profile-email-button">
                    <Mail size={13} /> Email
                  </a>
                  {business.website && (
                    <a href={business.website.startsWith("http") ? business.website : `https://${business.website}`} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-full border border-charcoal/25 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark" data-testid="profile-website-button">
                      <Globe size={13} /> Website
                    </a>
                  )}
                </div>

                <div className="mt-6 space-y-2 border-t border-stone_warm pt-6 text-xs text-charcoal/60">
                  <p className="flex items-center gap-2"><MapPin size={13} className="text-gold-dark" />{business.location}</p>
                  {business.social && <p className="flex items-center gap-2"><AtSign size={13} className="text-gold-dark" />{business.social.replace(/^@/, "")}</p>}
                  <p className="flex items-center gap-2">
                    <CalendarDays size={13} className="text-gold-dark" />
                    Member since {new Date(business.created_at).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                  </p>
                </div>
              </div>
            </motion.aside>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
              className="lg:col-span-8"
            >
              <h1 className="font-serif text-4xl font-light tracking-tighter text-charcoal sm:text-5xl lg:text-6xl" data-testid="profile-name">
                {business.business_name}
              </h1>
              <div className="gold-rule mt-8 w-40" aria-hidden="true" />
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-charcoal/75 sm:text-lg" data-testid="profile-story">
                {business.description}
              </p>

              <div className="mt-12 border border-stone_warm bg-white p-8" data-testid="profile-offerings">
                <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.28em] text-gold-dark">Products & services</p>
                <p className="text-base leading-relaxed text-charcoal/75">{business.offerings}</p>
              </div>

              {business.needs && (
                <div className="mt-6 flex items-start gap-4 border border-gold/40 bg-[#F3F0EB] p-8" data-testid="profile-needs">
                  <Handshake size={18} className="mt-1 shrink-0 text-gold-dark" />
                  <div>
                    <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.28em] text-gold-dark">Currently seeking</p>
                    <p className="text-base leading-relaxed text-charcoal/75">{business.needs}</p>
                  </div>
                </div>
              )}

              {business.gallery && business.gallery.length > 0 && (
                <div className="mt-12" data-testid="profile-gallery">
                  <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.28em] text-gold-dark">Gallery</p>
                  <div className="grid grid-cols-2 gap-4">
                    {business.gallery.map((g, i) => (
                      <figure key={g.path}>
                        <motion.img
                          initial={{ opacity: 0, y: 16 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{ duration: 0.7, ease: EASE, delay: (i % 2) * 0.08 }}
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${g.path}`}
                          alt={g.caption || `${business.business_name} photo ${i + 1}`}
                          className="aspect-[4/3] w-full border border-stone_warm object-cover"
                          data-testid={`gallery-image-${i}`}
                        />
                        {g.caption && (
                          <figcaption className="mt-2 font-serif text-sm font-light italic text-charcoal/55" data-testid={`gallery-caption-${i}`}>
                            {g.caption}
                          </figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 border border-stone_warm bg-white p-8" data-testid="referral-card">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.28em] text-gold-dark">Referral request</p>
                <h3 className="font-serif text-2xl font-medium tracking-tight text-charcoal">
                  Work with {business.business_name}
                </h3>
                {referralSent ? (
                  <p className="mt-5 text-sm leading-relaxed text-charcoal/70" data-testid="referral-success">
                    Your request has been sent to {business.owner_name} — expect to hear back soon.
                    Mention the Guild when you connect.
                  </p>
                ) : (
                  <form onSubmit={handleReferral} className="mt-6 space-y-4" data-testid="referral-form">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <input
                        required
                        value={referral.sender_name}
                        onChange={(e) => setReferral((r) => ({ ...r, sender_name: e.target.value }))}
                        placeholder="Your name"
                        className={inputCls}
                        data-testid="referral-name-input"
                      />
                      <input
                        required
                        value={referral.sender_contact}
                        onChange={(e) => setReferral((r) => ({ ...r, sender_contact: e.target.value }))}
                        placeholder="Your email or phone"
                        className={inputCls}
                        data-testid="referral-contact-input"
                      />
                    </div>
                    <textarea
                      required
                      rows={3}
                      value={referral.message}
                      onChange={(e) => setReferral((r) => ({ ...r, message: e.target.value }))}
                      placeholder="How can they help? A referral, a project, a partnership…"
                      className={inputCls}
                      data-testid="referral-message-input"
                    />
                    <button
                      type="submit"
                      disabled={referralSending}
                      className="inline-flex items-center gap-2.5 rounded-full bg-charcoal px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-gold disabled:opacity-60"
                      data-testid="referral-submit-button"
                    >
                      {referralSending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                      {referralSending ? "Sending…" : "Send request"}
                    </button>
                  </form>
                )}
              </div>

              <div className="mt-6 border border-stone_warm bg-white p-8" data-testid="testimonials-card">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.28em] text-gold-dark">Endorsements</p>
                <h3 className="font-serif text-2xl font-medium tracking-tight text-charcoal">
                  What fellow members say
                </h3>

                {testimonials.length > 0 ? (
                  <div className="mt-6 space-y-6" data-testid="testimonials-list">
                    {testimonials.map((t) => (
                      <blockquote key={t.id} className="border-l-2 border-gold/50 pl-5" data-testid={`testimonial-${t.id}`}>
                        <p className="font-serif text-lg font-light italic leading-relaxed text-charcoal/80">“{t.text}”</p>
                        <figcaption className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/50">
                          {t.author_name}{t.author_business ? ` — ${t.author_business}` : ""}
                        </figcaption>
                      </blockquote>
                    ))}
                  </div>
                ) : (
                  <p className="mt-5 text-sm text-charcoal/55" data-testid="testimonials-empty">
                    No endorsements yet — worked with {business.owner_name}? Be the first to vouch for them.
                  </p>
                )}

                {testimonialSent ? (
                  <p className="mt-6 border-t border-stone_warm pt-5 text-sm text-charcoal/70" data-testid="testimonial-success">
                    Thank you — your endorsement is now on their profile.
                  </p>
                ) : (
                  <form onSubmit={handleTestimonial} className="mt-6 space-y-4 border-t border-stone_warm pt-6" data-testid="testimonial-form">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <input
                        required
                        value={testimonialForm.author_name}
                        onChange={(e) => setTestimonialForm((f) => ({ ...f, author_name: e.target.value }))}
                        placeholder="Your name"
                        className={inputCls}
                        data-testid="testimonial-name-input"
                      />
                      <input
                        value={testimonialForm.author_business}
                        onChange={(e) => setTestimonialForm((f) => ({ ...f, author_business: e.target.value }))}
                        placeholder="Your business (optional)"
                        className={inputCls}
                        data-testid="testimonial-business-input"
                      />
                    </div>
                    <textarea
                      required
                      rows={2}
                      maxLength={500}
                      value={testimonialForm.text}
                      onChange={(e) => setTestimonialForm((f) => ({ ...f, text: e.target.value }))}
                      placeholder="A sentence or two about working with them…"
                      className={inputCls}
                      data-testid="testimonial-text-input"
                    />
                    <button
                      type="submit"
                      disabled={testimonialSending}
                      className="inline-flex items-center gap-2.5 rounded-full border border-charcoal/25 px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.22em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark disabled:opacity-60"
                      data-testid="testimonial-submit-button"
                    >
                      {testimonialSending ? <Loader2 size={13} className="animate-spin" /> : <Quote size={13} />}
                      {testimonialSending ? "Posting…" : "Leave an endorsement"}
                    </button>
                  </form>
                )}
              </div>

              <p className="mt-12 font-serif text-xl font-light italic text-charcoal/50">
                Mention the Joseph Guild when you reach out — it opens doors.
              </p>
            </motion.div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
